import React from 'react';
import { Button } from './ui/button';
import { ArrowRight } from 'lucide-react';
import { brandLogos } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';
import { useContent } from '../hooks/useContent';

const HeroSection = () => {
  const { t } = useLanguage();
  const { pick } = useContent('hero', {
    title1_en: t.hero.title1, title1_el: t.hero.title1,
    title2_en: t.hero.title2, title2_el: t.hero.title2,
    subtitle_en: t.hero.subtitle, subtitle_el: t.hero.subtitle,
    cta_en: t.hero.cta, cta_el: t.hero.cta,
    partnership_en: t.hero.partnership, partnership_el: t.hero.partnership,
  });

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a0e27] via-[#0d1832] to-[#0a1e2e]"></div>
      
      {/* Animated gradient orbs */}
      <div className="absolute top-20 right-20 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 left-20 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        {/* Main Heading */}
        <h1 className="font-bold text-white mb-6 leading-tight">
          <span className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl whitespace-nowrap">{pick('title1')}</span>
          <br />
          <span className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl bg-gradient-to-r from-cyan-400 to-blue-500 text-transparent bg-clip-text">
            {pick('title2')}
          </span>
        </h1>
        
        {/* Subheading */}
        <p className="text-xl md:text-2xl text-white/80 mb-8 max-w-4xl mx-auto">
          {pick('subtitle')}
        </p>
        
        {/* CTA Button */}
        <Button 
          asChild
          size="lg" 
          className="bg-white text-black hover:bg-white/90 text-lg px-8 py-6 rounded-lg group transition-all duration-300"
        >
          <a data-testid="hero-cta-mailto" href="mailto:contact@heliophant.com?subject=%CE%94%CF%89%CF%81%CE%B5%CE%AC%CE%BD%20%CE%94%CE%BF%CE%BA%CE%B9%CE%BC%CE%AE%2030%20%CE%97%CE%BC%CE%B5%CF%81%CF%8E%CE%BD&body=%CE%93%CE%B5%CE%B9%CE%B1%20%CF%83%CE%B1%CF%82%2C%0A%0A%CE%98%CE%B1%20%CE%AE%CE%B8%CE%B5%CE%BB%CE%B1%20%CE%BD%CE%B1%20%CE%B4%CE%BF%CE%BA%CE%B9%CE%BC%CE%AC%CF%83%CF%89%20%CF%84%CE%B7%CE%BD%20%CF%85%CF%80%CE%B7%CF%81%CE%B5%CF%83%CE%AF%CE%B1%20%CF%83%CE%B1%CF%82%20%CE%B3%CE%B9%CE%B1%2030%20%CE%BC%CE%AD%CF%81%CE%B5%CF%82%20%CE%B4%CF%89%CF%81%CE%B5%CE%AC%CE%BD.%0A%0A%CE%A0%CE%B1%CF%81%CE%B1%CE%BA%CE%B1%CE%BB%CF%8E%20%CE%B5%CF%80%CE%B9%CE%BA%CE%BF%CE%B9%CE%BD%CF%89%CE%BD%CE%AE%CF%83%CF%84%CE%B5%20%CE%BC%CE%B1%CE%B6%CE%AF%20%CE%BC%CE%BF%CF%85.%0A%0A%CE%95%CF%85%CF%87%CE%B1%CF%81%CE%B9%CF%83%CF%84%CF%8E">
            {pick('cta')}
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
          </a>
        </Button>
        
        {/* Brand Logos */}
        <div className="mt-20">
          <p className="text-white/50 text-sm mb-4">{pick('partnership')}</p>
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
            {brandLogos.map((logo) => (
              <a 
                key={logo.name} 
                href={logo.name === 'TradingView' ? 'https://www.tradingview.com/chart/lFvfsrpC/' : '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="h-16 hover:opacity-80 transition-all duration-300 cursor-pointer"
              >
                <img 
                  src={logo.src} 
                  alt={logo.name} 
                  className="h-full w-auto object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </a>
            ))}
          </div>
        </div>
      </div>
      
      {/* Decorative bottom wave */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#050811] to-transparent"></div>
    </section>
  );
};

export default HeroSection;